import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const POSITION_KEY = "geikoChatPosition";

const readSavedPosition = () => {
  try {
    const raw = sessionStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.left === "number" &&
      typeof parsed?.top === "number" &&
      Number.isFinite(parsed.left) &&
      Number.isFinite(parsed.top)
    ) {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore invalid session data
  }
  return null;
};

const clampPosition = (left, top, width, height) => {
  const maxLeft = Math.max(0, window.innerWidth - width);
  const maxTop = Math.max(0, window.innerHeight - height);
  return {
    left: Math.min(Math.max(0, left), maxLeft),
    top: Math.min(Math.max(0, top), maxTop),
  };
};

const isChatVisible = (chat) => {
  if (!chat) return false;
  if (chat.classList.contains("topcornerGeikoActive")) return true;
  const display = chat.style.display;
  return display === "block" || display === "flex";
};

export const GeikoChatHost = () => {
  const shellRef = useRef(null);
  const chatRef = useRef(null);
  const dragStateRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const applyDefaultPosition = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return;
    shell.style.left = "";
    shell.style.top = "";
    shell.style.bottom = "0";
    shell.style.right = "318px";
  }, []);

  const applyPosition = useCallback(
    (position) => {
      const shell = shellRef.current;
      if (!shell || !position) {
        applyDefaultPosition();
        return;
      }
      const rect = shell.getBoundingClientRect();
      const clamped = clampPosition(
        position.left,
        position.top,
        rect.width || 440,
        rect.height || 100
      );
      shell.style.bottom = "auto";
      shell.style.right = "auto";
      shell.style.left = `${clamped.left}px`;
      shell.style.top = `${clamped.top}px`;
    },
    [applyDefaultPosition]
  );

  const syncShellVisibility = useCallback(() => {
    const chat = chatRef.current;
    const shell = shellRef.current;
    if (!chat || !shell) return;

    const active = isChatVisible(chat);
    shell.classList.toggle("geiko-chat-shell--active", active);
    setIsActive(active);

    if (active) {
      const saved = readSavedPosition();
      if (saved) {
        requestAnimationFrame(() => applyPosition(saved));
      } else {
        applyDefaultPosition();
      }
    } else {
      applyDefaultPosition();
    }
  }, [applyDefaultPosition, applyPosition]);

  useEffect(() => {
    const chat = chatRef.current;
    if (!chat) return;

    // Managed by Geiko scripts after mount — do not set via React style props
    chat.style.display = "none";

    syncShellVisibility();

    const observer = new MutationObserver(() => {
      syncShellVisibility();
    });

    observer.observe(chat, {
      attributes: true,
      attributeFilter: ["class", "style"],
      childList: true,
    });

    return () => observer.disconnect();
  }, [syncShellVisibility]);

  useEffect(() => {
    const onResize = () => {
      const shell = shellRef.current;
      if (!shell || !isActive) return;
      if (shell.style.left === "" && shell.style.top === "") return;
      const rect = shell.getBoundingClientRect();
      applyPosition({ left: rect.left, top: rect.top });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [applyPosition, isActive]);

  const setIframePointerEvents = (value) => {
    const iframe = document.getElementById("IframeChatNovo");
    if (iframe) {
      iframe.style.pointerEvents = value;
    }
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    const shell = shellRef.current;
    if (!shell) return;

    const rect = shell.getBoundingClientRect();
    dragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };

    setIsDragging(true);
    setIframePointerEvents("none");
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    if (!isDragging) return;

    const onPointerMove = (event) => {
      const shell = shellRef.current;
      const drag = dragStateRef.current;
      if (!shell || !drag) return;

      const next = clampPosition(
        event.clientX - drag.offsetX,
        event.clientY - drag.offsetY,
        drag.width,
        drag.height
      );

      shell.style.bottom = "auto";
      shell.style.right = "auto";
      shell.style.left = `${next.left}px`;
      shell.style.top = `${next.top}px`;
    };

    const endDrag = () => {
      const shell = shellRef.current;
      if (shell) {
        const rect = shell.getBoundingClientRect();
        const position = { left: rect.left, top: rect.top };
        try {
          sessionStorage.setItem(POSITION_KEY, JSON.stringify(position));
        } catch {
          // ignore quota / private mode errors
        }
      }
      dragStateRef.current = null;
      setIsDragging(false);
      setIframePointerEvents("");
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [isDragging]);

  return createPortal(
    <div
      id="geikoChatShell"
      ref={shellRef}
      className={`geiko-chat-shell${
        isActive ? " geiko-chat-shell--active" : ""
      }${isDragging ? " geiko-chat-shell--dragging" : ""}`}
    >
      <div
        className="geiko-chat-drag-handle"
        onPointerDown={handlePointerDown}
        role="presentation"
        aria-hidden={!isActive}
      >
        <span className="geiko-chat-drag-handle__grip" />
      </div>
      <div id="chatGeiko" ref={chatRef} className="topcornerGeiko" />
    </div>,
    document.body
  );
};
